import {
  Button,
  FormControlLabel,
  IconButton,
  InputBase,
  Paper,
  Switch,
  Typography
} from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import PageHeader from '../../../components/page/pageHeader'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import SearchIcon from '@material-ui/icons/Search'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { styles } from '../clientes.styles'
import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  Form,
  UserPermission
} from '@globaleris/ui.library'
import {
  getCustomerDomain,
  notifyError,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage,
  storeShowOnlyActivesFilterForCurrentPage,
  storeSearchText,
  getSearchText
} from '../../../redux'
import { getUserPermissions } from '../../../helpers/permissionsHelper'
import { DataTableErrorHandler } from '../../../components/errors/scopes/dataTable/dataTableError'
import Page from '../../../components/page/page'
import { Pages } from '../../../model/RBAC/constants'
import { useHttpError } from '../../../hooks/useHttpError'
import { SubjectTableModel } from '../clients.model'
import { usePageState } from '../../../hooks'
import { fetchClients } from '../client.service'
import { EnumCommodity, EnumTipoSujeto, EnumEstadoCliente } from '../../../api'
import { Edit, NoteAdd, AssignmentInd } from '@material-ui/icons'
import { enumEnumToValueString, enumTranslateOption } from '../../../helpers'

interface ClientsSearchPageProps {
  location: { state: { subjectType: EnumTipoSujeto; searchText?: string } }
}

export const ClientsSearchPage: React.FC<ClientsSearchPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'clients', 'enums'])
  const [subjectType] = useState<EnumTipoSujeto>(
    props.location.state !== undefined && props.location.state.subjectType
      ? props.location.state.subjectType
      : EnumTipoSujeto.ClienteFinal
  )
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { dataTableFilter, dataTableSearch, showOnlyActivesFilter } = usePageState()

  const history = useHistory()
  const { errorProps } = useHttpError()

  // To fetch persisted Text from Redux to use it as Initial searchText value
  const persistedSearchTextBox = getSearchText()

  const [searchText, setSearchText] = useState<string>(persistedSearchTextBox || '')
  const [showOnlyActives, setShowOnlyActives] = useState<boolean>(
    showOnlyActivesFilter === undefined ? true : showOnlyActivesFilter
  )
  const [clients, setClients] = useState<SubjectTableModel[] | undefined>([])
  const [searchError, setSearchError] = useState<boolean>(false)

  // Functions Region Starts

  const searchClients = async (showOnlyActives: boolean) => {
    if (searchText) {
      setClients(undefined)
      setIsFetching(true)
      const items = await countItems(showOnlyActives, undefined)
      if (items == null || items?.length > 1000) {
        notifyError(t('clients:searchPage.notifications.invalidFilterError'))
        setClients([])
      } else {
        setClients(
          items?.map((item: SubjectTableModel) => {
            const contractCodeWithState = item.contractsList.map(contract => {
              let contractCodeString = contract.code
              if (contract.status) {
                contractCodeString = `${contract.code} (${enumTranslateOption(contract.status, EnumEstadoCliente, 'enums:EnumEstadoCliente')})`
              }
              return contractCodeString
            })
            return {
              ...item,
              contractsCode: contractCodeWithState.join(', '),
              cupsCode: item.cupsCodeList?.join(', ')
            }
          })
        )
      }
      setIsFetching(false)
    } else {
      notifyError(t('clients:searchPage.notifications.invalidSearchText'))
    }
  }

  const onChangeSearchText = (event: any) => {
    setSearchText(event.target.value)

    // To persist search Text store it:
    storeSearchText(event.target.value)
  }

  const onChangeShowOnlyActives = (event: any) => {
    setShowOnlyActives(!event.target.checked)
    searchClients(!event.target.checked)
    storeShowOnlyActivesFilterForCurrentPage(!event.target.checked)
  }

  const handleSearch = () => {
    // if (searchText === '') {
    //   notifyError(t('clients:searchPage.notifications.invalidSearchText'))
    //   return
    // }

    searchClients(showOnlyActives)
  }

  useEffect(() => {
    if (searchText !== '' && showOnlyActives !== undefined) {
      searchClients(showOnlyActives)
    }
  }, [])

  const countItems = async (
    showOnlyActives?: boolean,
    commodity?: EnumCommodity
  ): Promise<SubjectTableModel[]> => {
    return await fetchClients(searchText!, [subjectType], !!showOnlyActives, commodity).then(
      response => {
        if (response.success) {
          setSearchError(false)
          if (response.data !== undefined) {
            return response.data
          } else {
            return []
          }
        } else {
          setSearchError(true)
          return null
        }
      }
    )
  }

  //Functions Region Ends

  // DataTable Config Starts

  const columns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'id',
        display: false
      },
      {
        name: 'fiscalDocument',
        label: t('clients:searchPage.table.columns.fiscalDocument'),
        type: DataType.TEXT,
        columnWidth: 100
      },
      {
        name: 'description',
        label: t('clients:searchPage.table.columns.description'),
        type: DataType.LONGTEXT,
        columnWidth: 150
      },
      {
        name: 'contractsCode',
        label: t('clients:searchPage.table.columns.contractCodeList'),
        type: DataType.LONGTEXT,
        columnWidth: 200
      },
      {
        name: 'cupsCode',
        label: t('clients:searchPage.table.columns.cupsCodeList'),
        type: DataType.LONGTEXT,
        columnWidth: 200
      },
      {
        name: 'commodityType',
        label: t('clients:searchPage.table.columns.commodity'),
        type: DataType.TEXT
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const options = React.useMemo((): DataTableOptions => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: true,
      elevation: 1,
      filterType: FilterType.ADVANCED,
      download: true,
      canEdit: false,
      canDelete: false,
      canInsert: false,
      customInsertEditModal: false,
      analytics: {
        customer: getCustomerDomain(),
        view: t('clients:searchPage.table.analytics')
      },
      onFilterChange: (filter: ColumnFilter[]) => {
        storeDataTableFilterForCurrentPage(filter)
      },
      onSearchChange: (search: string) => {
        storeDataTableSearchForCurrentPage(search)
      },
      rowCustomActions: [
        {
          icon: <AssignmentInd fontSize="small" />,
          tooltip: 'ver Ficha',
          onClick: (row: SubjectTableModel) => {
            history.push(NonAuthRoutes.ClientFile, {
              clientID: row.id,
              contractsIDs: row.contractIDList
            })
          }
          // userPermission: UserPermission.EDIT
        },
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('clients:searchPage.table.customButtons.editClient'),
          onClick: (row: SubjectTableModel) => {
            //history.push(NonAuthRoutes.ClientEdit, { clientID: row.id });
            if (subjectType) {
              if (subjectType === EnumTipoSujeto.ClienteFinal) {
                history.push(NonAuthRoutes.clientsModEdit, { idCliente: row.id })
              } else if (subjectType === EnumTipoSujeto.Mayorista) {
                history.push(NonAuthRoutes.partnersEdit, { idCliente: row.id })
              } else if (subjectType === EnumTipoSujeto.Comercial) {
                history.push(NonAuthRoutes.CommercialEdit, { commercialId: row.id })
              } else {
                history.push(NonAuthRoutes.clientsModEdit, { idCliente: row.id })
              }
            } else {
              history.push(NonAuthRoutes.clientsModEdit, { idCliente: row.id })
            }
          },
          userPermission: UserPermission.EDIT
        }
      ]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [])

  // If we pass getUserPermissions directly on the return, it makes a new object on each and every render (for example, typing on the search input, on every keystroke),
  // triggering unnecessary useEffect inside the datatable.
  // This return will be the same every time, unless user permissions are changed on DDBB.
  const memoizedUserPermissions = React.useMemo(() => {
    return getUserPermissions(Pages.ClientsSearch)
  }, [])

  //DataTable Config Ends

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('clients:titleSearch')}>
        <Button
          variant="text"
          color="primary"
          disabled={isFetching}
          onClick={() => {
            history.push(NonAuthRoutes.Client)
          }}
        >
          {t('clients:searchPage.buttons.back')}
        </Button>
      </PageHeader>
      <Paper className={classes.root}>
        <InputBase
          className={classes.input}
          disabled={isFetching}
          value={searchText}
          placeholder={t('clients:searchPage.searchBar')}
          inputProps={{ 'aria-label': 'search clients' }}
          onChange={onChangeSearchText}
          onKeyUp={event => {
            if (event.key === 'Enter') {
              handleSearch()
            }
          }}
        />
        <IconButton
          type="button"
          className={classes.iconButton}
          aria-label="search"
          onClick={handleSearch}
          disabled={isFetching}
        >
          <SearchIcon />
        </IconButton>
      </Paper>
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            size="small"
            checked={!showOnlyActives}
            onChange={onChangeShowOnlyActives}
            name="unsuscribed"
            color="primary"
            disabled={isFetching}
            inputProps={{ 'aria-label': 'unsuscribed clients' }}
          />
        }
        label={
          <Typography variant="subtitle2" color="secondary">
            {t('clients:searchPage.filters.unsuscribed')}
          </Typography>
        }
        disabled={isFetching}
      />
      {searchError && (
        <Typography variant="subtitle2" color="error" className={classes.searchErrorMsg}>
          {t('clients:searchPage.errorMsg')}
        </Typography>
      )}
      <DataTable
        className={classes.table}
        data={clients}
        columns={columns}
        options={options}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={memoizedUserPermissions}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />
    </Page>
  )
}
